import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Truck, Package, Printer, XCircle, Copy, ChevronDown, ChevronUp, PrinterCheck } from "lucide-react";

/**
 * AdminFulfillment — "Ready to Ship" queue.
 *
 * Lists every PAID order that still needs a shipping label, plus a
 * per-order rate-picker and one-click "Buy label" flow via Shippo.
 * Uses:
 *   · GET  /admin/fulfillment/queue
 *   · POST /admin/fulfillment/orders/{id}/refresh-rates
 *   · POST /admin/fulfillment/orders/{id}/buy-label
 *   · POST /admin/fulfillment/orders/{id}/void-label
 *
 * The purchased label URL opens in a new tab so the admin can print the
 * 4×6 PDF directly. Tracking number + carrier are auto-attached to the
 * order (which fires the existing tracking-email + notification pipeline).
 */
export default function AdminFulfillment() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // orderId -> bool
  const [busyId, setBusyId] = useState(null); // orderId currently fetching/buying
  const [picked, setPicked] = useState({}); // orderId -> option_id
  const [includeShipped, setIncludeShipped] = useState(false);
  // iter138.2 — checkbox selection for bulk label printing. Only meaningful
  // for orders that already have a purchased label.
  const [selected, setSelected] = useState({}); // orderId -> bool
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fulfillment/queue", {
        params: { include_shipped: includeShipped },
      });
      setOrders(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't load fulfillment queue.");
    } finally {
      setLoading(false);
    }
  }, [includeShipped]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const refreshRates = async (order) => {
    setBusyId(order.id);
    try {
      const { data } = await api.post(`/admin/fulfillment/orders/${order.id}/refresh-rates`);
      setOrders((list) => list.map((o) => o.id === order.id ? {
        ...o,
        fulfillment_rates: data.options,
        fulfillment_rates_at: data.fetched_at,
      } : o));
      if (data.options?.length) {
        const cheapest = data.options.reduce((a, b) => a.rate_cents <= b.rate_cents ? a : b);
        setPicked((p) => ({ ...p, [order.id]: cheapest.option_id }));
      }
      toast.success(`Loaded ${data.options?.length || 0} rates.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Couldn't fetch rates.");
    } finally {
      setBusyId(null);
    }
  };

  const buyLabel = async (order) => {
    const optionId = picked[order.id];
    if (!optionId) {
      toast.error("Pick a rate first.");
      return;
    }
    setBusyId(order.id);
    try {
      const { data } = await api.post(
        `/admin/fulfillment/orders/${order.id}/buy-label`,
        { option_id: optionId }
      );
      setOrders((list) => list.map((o) => o.id === order.id ? data.order : o));
      toast.success(`Label purchased — ${data.tracking_number || "no tracking #"}`);
      if (data.label_url) {
        window.open(data.label_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Label purchase failed.");
    } finally {
      setBusyId(null);
    }
  };

  const buyBuyerLabel = async (order) => {
    setBusyId(order.id);
    try {
      const { data } = await api.post(
        `/admin/fulfillment/orders/${order.id}/buy-buyer-label`
      );
      setOrders((list) => list.map((o) => o.id === order.id ? data.order : o));
      toast.success(`Label purchased — ${data.tracking_number || "no tracking #"}`);
      if (data.label_url) {
        window.open(data.label_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Label purchase failed.");
    } finally {
      setBusyId(null);
    }
  };

  const voidLabel = async (order) => {
    if (!window.confirm(`Refund the label for ${order.order_number}? Only unused labels can be refunded.`)) return;
    setBusyId(order.id);
    try {
      const { data } = await api.post(`/admin/fulfillment/orders/${order.id}/void-label`);
      toast.success(`Refund requested (${data.refund?.status || "queued"}).`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Void failed.");
    } finally {
      setBusyId(null);
    }
  };

  const readyCount = useMemo(
    () => orders.filter((o) => !o.shippo_transaction_id).length,
    [orders]
  );

  const labeledOrders = useMemo(
    () => orders.filter((o) => !!o.shippo_transaction_id && !!o.label_url),
    [orders]
  );
  const selectedIds = useMemo(
    () => labeledOrders.filter((o) => selected[o.id]).map((o) => o.id),
    [labeledOrders, selected]
  );
  const allSelected = labeledOrders.length > 0 && selectedIds.length === labeledOrders.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      labeledOrders.forEach((o) => { next[o.id] = true; });
      setSelected(next);
    }
  };

  const printAllSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error("Pick at least one order with a purchased label.");
      return;
    }
    setBulkBusy(true);
    try {
      // Blob response — merged PDF served straight from the backend.
      const res = await api.post(
        "/admin/fulfillment/bulk-labels",
        { order_ids: selectedIds },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Open in new tab so admin can print or save. Chrome auto-opens the print
      // dialog on Ctrl+P; we don't force it here because some admins prefer to
      // review the merged PDF first.
      window.open(url, "_blank", "noopener,noreferrer");
      const failedHeader = res.headers["x-bbk-labels-failed"] || "";
      const failed = failedHeader ? failedHeader.split(",").filter(Boolean) : [];
      if (failed.length) {
        toast.warning(`${selectedIds.length - failed.length} labels merged · ${failed.length} skipped (${failed.join(", ")})`);
      } else {
        toast.success(`Merged ${selectedIds.length} label${selectedIds.length === 1 ? "" : "s"} into one PDF.`);
      }
      setSelected({});
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Bulk print failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-fulfillment">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-3xl mb-1 flex items-center gap-2">
            <Truck size={22} strokeWidth={1.7} className="text-oxblood" />
            Ready to Ship
          </h2>
          <p className="text-sm text-charcoal/70 max-w-xl">
            Paid orders that still need a shipping label. Fetch live Shippo rates (USPS + UPS), buy a 4×6 PDF label with one click, and the buyer gets an auto-tracking email.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-[13px] text-charcoal/80 select-none">
            <input
              type="checkbox"
              className="accent-oxblood"
              checked={includeShipped}
              onChange={(e) => setIncludeShipped(e.target.checked)}
              data-testid="fulfillment-include-shipped"
            />
            Include shipped
          </label>
          <button
            type="button"
            onClick={load}
            className="text-[13px] uppercase tracking-[0.22em] font-mono text-charcoal hover:text-oxblood inline-flex items-center gap-1.5"
            data-testid="fulfillment-refresh-queue"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="fulfillment-stats-row">
        <div className="flex items-center gap-3 text-[13px] text-charcoal/70" data-testid="fulfillment-stats">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-oxblood/10 text-oxblood font-mono uppercase tracking-widest text-[11px]">
            <Package size={11} /> {readyCount} to label
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-mono uppercase tracking-widest text-[11px]">
            {orders.length - readyCount} labelled
          </span>
        </div>
        {labeledOrders.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="fulfillment-bulk-bar">
            <label className="flex items-center gap-2 text-[13px] text-charcoal/80 select-none cursor-pointer">
              <input
                type="checkbox"
                className="accent-oxblood w-4 h-4"
                checked={allSelected}
                onChange={toggleSelectAll}
                data-testid="fulfillment-select-all"
              />
              Select all labelled
            </label>
            <button
              type="button"
              onClick={printAllSelected}
              disabled={selectedIds.length === 0 || bulkBusy}
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-oxblood hover:bg-oxblood/90 text-bone tracking-[0.22em] text-[11px] uppercase rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="fulfillment-print-all-selected"
            >
              <PrinterCheck size={13} />
              {bulkBusy ? "Merging…" : `Print all selected${selectedIds.length ? ` · ${selectedIds.length}` : ""}`}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-[15px] text-charcoal/70" data-testid="fulfillment-loading">Loading queue…</p>
      ) : orders.length === 0 ? (
        <div className="border border-dashed border-borderSubtle rounded-2xl p-10 text-center text-charcoal/70" data-testid="fulfillment-empty">
          <p className="font-serif text-2xl mb-2">Nothing to ship right now.</p>
          <p className="text-[14px]">Orders will land here the moment they're marked paid.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const isOpen = !!expanded[o.id];
            const hasLabel = !!o.shippo_transaction_id;
            const rates = o.fulfillment_rates || [];
            const chosen = picked[o.id];
            const isBusy = busyId === o.id;
            return (
              <div
                key={o.id}
                className={`border rounded-2xl overflow-hidden bg-white ${hasLabel ? "border-emerald-200" : "border-borderSubtle"}`}
                data-testid={`fulfillment-order-${o.order_number}`}
              >
                {/* Row header */}
                <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
                  <div className="min-w-0 flex items-start gap-3">
                    {hasLabel && (
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 accent-oxblood shrink-0"
                        checked={!!selected[o.id]}
                        onChange={(e) => setSelected((s) => ({ ...s, [o.id]: e.target.checked }))}
                        data-testid={`fulfillment-select-${o.order_number}`}
                        aria-label={`Select ${o.order_number} for bulk print`}
                      />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[15px] font-bold text-charcoal">{o.order_number}</span>
                      {hasLabel ? (
                        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Label bought
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-oxblood/10 text-oxblood border border-oxblood/30">
                          Awaiting label
                        </span>
                      )}
                      {o.cart_tier === "select" && (
                        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#DDA94E]/15 text-[#6E521A] border border-[#DDA94E]/40">
                          R-1617
                        </span>
                      )}
                    </div>
                    <p className="text-[13px] text-charcoal/70 truncate">
                      {o.customer_name || "—"} · {o.customer_email} · <span className="font-mono">${o.total.toFixed(2)}</span> · {o.kits} kit{o.kits === 1 ? "" : "s"}
                    </p>
                    <p className="text-[12px] text-charcoal/60 truncate">
                      Ship to: {o.address.line1}, {o.address.city} {o.address.state} {o.address.postal_code}
                    </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {hasLabel ? (
                      <>
                        <a
                          href={o.label_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-[13px] font-mono uppercase tracking-[0.18em] text-charcoal hover:text-oxblood"
                          data-testid={`fulfillment-print-${o.order_number}`}
                        >
                          <Printer size={14} /> Print label
                        </a>
                        {o.tracking_number && (
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard?.writeText(o.tracking_number);
                              toast.success(`Copied ${o.tracking_number}`);
                            }}
                            className="inline-flex items-center gap-1.5 text-[13px] font-mono uppercase tracking-[0.18em] text-charcoal hover:text-oxblood"
                            data-testid={`fulfillment-copy-tracking-${o.order_number}`}
                          >
                            <Copy size={14} /> {o.tracking_number}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => voidLabel(o)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-widest text-charcoal/60 hover:text-oxblood disabled:opacity-50"
                          data-testid={`fulfillment-void-${o.order_number}`}
                        >
                          <XCircle size={13} /> Void
                        </button>
                      </>
                    ) : o.buyer_chosen_rate?.usable ? (
                      /* iter142 P1B — one-click purchase of buyer's picked rate. */
                      <>
                        <div className="text-right leading-tight mr-1">
                          <p className="text-[11px] font-mono uppercase tracking-widest text-charcoal/60">Buyer picked</p>
                          <p className="text-[13px] text-charcoal font-medium">{o.buyer_chosen_rate.service_display} <span className="font-mono text-oxblood ml-1">${(o.buyer_chosen_rate.rate_cents / 100).toFixed(2)}</span></p>
                        </div>
                        <button
                          type="button"
                          onClick={() => buyBuyerLabel(o)}
                          disabled={isBusy}
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-oxblood hover:bg-oxblood/90 text-bone tracking-[0.22em] text-[11px] uppercase rounded-full transition-colors disabled:opacity-50"
                          data-testid={`fulfillment-buy-buyer-${o.order_number}`}
                        >
                          {isBusy ? "Buying…" : "Buy label"}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggle(o.id)}
                          className="text-[11px] uppercase tracking-widest text-charcoal/60 hover:text-oxblood"
                          data-testid={`fulfillment-change-rate-${o.order_number}`}
                          title="Override buyer's rate (re-quote)"
                        >
                          Change rate
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggle(o.id)}
                        className="inline-flex items-center gap-1.5 text-[13px] font-mono uppercase tracking-[0.18em] text-charcoal hover:text-oxblood"
                        data-testid={`fulfillment-toggle-${o.order_number}`}
                      >
                        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {isOpen ? "Hide" : "Get rates"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Rate picker (only when open + no label yet) */}
                {isOpen && !hasLabel && (
                  <div className="border-t border-borderSubtle bg-bone/40 px-5 py-4" data-testid={`fulfillment-rates-${o.order_number}`}>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[13px] text-charcoal/70">
                        {rates.length > 0
                          ? `${rates.length} live rates from Shippo · quoted ${o.fulfillment_rates_at ? new Date(o.fulfillment_rates_at).toLocaleTimeString() : "just now"}.`
                          : "Fetch live Shippo rates for this destination."}
                      </p>
                      <button
                        type="button"
                        onClick={() => refreshRates(o)}
                        disabled={isBusy}
                        className="text-[12px] uppercase tracking-widest font-mono text-charcoal hover:text-oxblood disabled:opacity-50 inline-flex items-center gap-1.5"
                        data-testid={`fulfillment-refresh-rates-${o.order_number}`}
                      >
                        <RefreshCw size={13} className={isBusy ? "animate-spin" : ""} />
                        {rates.length > 0 ? "Refresh rates" : "Get rates"}
                      </button>
                    </div>

                    {rates.length > 0 && (
                      <>
                        <div className="space-y-2 mb-4">
                          {rates.map((r) => (
                            <label
                              key={r.option_id}
                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                chosen === r.option_id
                                  ? "border-oxblood bg-oxblood/5"
                                  : "border-borderSubtle bg-white hover:border-charcoal"
                              }`}
                              data-testid={`fulfillment-rate-${o.order_number}-${r.carrier}-${r.service}`}
                            >
                              <input
                                type="radio"
                                className="w-4 h-4 accent-oxblood"
                                name={`rate-${o.id}`}
                                checked={chosen === r.option_id}
                                onChange={() => setPicked((p) => ({ ...p, [o.id]: r.option_id }))}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-[15px] font-medium text-charcoal">{r.service_display}</p>
                                {r.delivery_days != null && (
                                  <p className="text-[12px] text-charcoal/70">Est. {r.delivery_days} business day{r.delivery_days === 1 ? "" : "s"}</p>
                                )}
                              </div>
                              <span className="font-mono font-semibold text-[15px] text-charcoal whitespace-nowrap">
                                ${(r.rate_cents / 100).toFixed(2)}
                              </span>
                            </label>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => buyLabel(o)}
                          disabled={!chosen || isBusy}
                          className="px-5 py-2.5 bg-oxblood hover:bg-oxblood/90 text-bone tracking-[0.22em] text-[12px] uppercase rounded-full transition-colors disabled:opacity-50"
                          data-testid={`fulfillment-buy-${o.order_number}`}
                        >
                          {isBusy ? "Buying…" : "Buy label + attach tracking"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
